import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, productImage, TYPE_LABELS, scoreColor } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, FileText, Send, Undo2, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const COLS = [
  { key: "review", title: "In Review", accent: "border-t-purple-500", tint: "bg-purple-50 text-purple-700" },
  { key: "draft", title: "Etsy Drafts", accent: "border-t-amber-500", tint: "bg-amber-50 text-amber-700" },
  { key: "published", title: "Published", accent: "border-t-emerald-500", tint: "bg-emerald-50 text-emerald-700" },
];

export default function ApprovalQueue() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/products").then(({ data }) => { setProducts(data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const move = async (p, state) => {
    await api.patch(`/products/${p.id}`, { workflow_state: state });
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, workflow_state: state } : x)));
    toast.success(state === "published" ? "Published to Etsy" : `Moved to ${state}`);
  };

  const byState = (s) => products.filter((p) => p.workflow_state === s);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="approval-page">
      <div className="mb-5">
        <p className="overline text-blue-600">Approval Queue</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Review &amp; publish</h1>
        <p className="text-sm text-zinc-500 mt-1">Move products through review → Etsy draft → published.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {COLS.map((col) => (
          <div key={col.key} className={`bg-zinc-100/60 rounded-lg border-t-2 ${col.accent} p-3`} data-testid={`col-${col.key}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="font-display font-bold text-sm tracking-tight">{col.title}</span>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${col.tint}`}>{byState(col.key).length}</span>
            </div>
            <div className="space-y-2.5 min-h-[120px]">
              {byState(col.key).map((p) => (
                <div key={p.id} data-testid={`card-${p.id}`} className="bg-white border border-zinc-200 rounded-lg p-3 hover:border-zinc-300 transition-colors">
                  <div className="flex gap-2.5">
                    <img src={productImage(p)} alt="" className="w-12 h-12 rounded-md object-cover bg-zinc-100 shrink-0 cursor-pointer" onClick={() => navigate(`/products?id=${p.id}`)} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <Badge variant="secondary" className="text-[9px]">{TYPE_LABELS[p.product_type]}</Badge>
                        {p.automation_mode === "auto" && <span className="text-[9px] text-blue-600 flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />auto</span>}
                      </div>
                      <div className="text-xs font-semibold mt-1 leading-tight line-clamp-2">{p.listing_title}</div>
                    </div>
                    <span className={`text-[10px] font-bold shrink-0 ${scoreColor(p.impulse_score)}`}>{p.impulse_score}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-zinc-100">
                    {col.key === "review" && (
                      <>
                        <button onClick={() => move(p, "draft")} data-testid={`to-draft-${p.id}`} className="flex-1 h-7 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-medium flex items-center justify-center gap-1 transition-colors"><FileText className="w-3 h-3" /> Draft</button>
                        <button onClick={() => move(p, "published")} data-testid={`to-pub-${p.id}`} className="flex-1 h-7 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-medium flex items-center justify-center gap-1 transition-colors"><Send className="w-3 h-3" /> Publish</button>
                      </>
                    )}
                    {col.key === "draft" && (
                      <>
                        <button onClick={() => move(p, "review")} className="h-7 w-8 rounded-md border border-zinc-200 hover:bg-zinc-50 text-zinc-500 flex items-center justify-center transition-colors"><Undo2 className="w-3 h-3" /></button>
                        <button onClick={() => move(p, "published")} data-testid={`draft-to-pub-${p.id}`} className="flex-1 h-7 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-medium flex items-center justify-center gap-1 transition-colors"><Send className="w-3 h-3" /> Publish</button>
                      </>
                    )}
                    {col.key === "published" && (
                      <button onClick={() => navigate(`/products?id=${p.id}`)} className="flex-1 h-7 rounded-md border border-zinc-200 hover:bg-zinc-50 text-[11px] font-medium text-zinc-600 flex items-center justify-center gap-1 transition-colors">View listing <ArrowRight className="w-3 h-3" /></button>
                    )}
                  </div>
                </div>
              ))}
              {byState(col.key).length === 0 && <div className="text-xs text-zinc-400 text-center py-8">Nothing here yet</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
